import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import * as yup from 'yup';
import FileBase64 from 'react-file-base64';

import { IFile, IUser, Newsletter } from '../../../globals/interfaces';
import BackButton from '../../../components/custom/BackButton';
import { createdUpdatedInfo, getTsDate, notify } from '../../../utils';
import ActionIcon from '../../../components/custom/ActionIcon';
import useTypedForm from '../../../components/custom/hookForm/useTypedForm';
import _ from 'lodash';
import moment from 'moment';
import { yupResolver } from '@hookform/resolvers/yup';
import useLoading from '../../../customHooks/useLoading';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    data: Array<Newsletter>;
    item: Newsletter | undefined;
    user: IUser;
    createNewsletter: (data: Newsletter) => Promise<void>;
    updateNewsletter: (data: Newsletter) => Promise<void>;
    deleteNewsletter: (id: number) => Promise<void>;
}

export default function ModalManageNewsletter ({
    modal,
    setModal,
    data,
    item,
    user,
    createNewsletter,
    updateNewsletter,
    deleteNewsletter
}: Props) {

    const [loading, setLoading] = useState(false);
    const update = Boolean(item);
    const { 
        formState: {
            errors,
            isValid,
            isDirty
        },
        reset, 
        trigger,
        setValue,
        handleSubmit,
        FormInput,
        watch
    } = useTypedForm<Newsletter>({
        mode: 'all',
        resolver: yupResolver(yup.object().shape({
            name: yup.string().required(),
            year: yup.number().required(),
            issue: yup.number().required(),
            createdBy: yup.string().required(),
            createdAt: yup.date().required(),
            modifiedBy: yup.string().required(),
            modifiedAt: yup.date().required(),
            file: yup.object().notRequired().required().shape({
                base64: yup.string().required(),
                type: yup.string().required()
            })
        }))
    });

    useEffect(() => {
        if (item) {
            const copy = _.cloneDeep(item);
            reset(copy);
        } else {
            reset({
                name: '',
                year: moment().year(),
                issue: data.length ? data[0].issue + 1 : 1,
                link: '',
                createdBy: user.s_email,
                createdAt: getTsDate(),
                modifiedBy: user.s_email,
                modifiedAt: getTsDate(),
                file: undefined
            });
        }
        trigger();
    }, [modal, data, item]);

    const handleUploadFile = (file: IFile) => {
        setValue('file', file, {
            shouldValidate: true,
            shouldDirty: true
        });
    }

    const handleCreateUpdate = async (item: Newsletter) => {
        const fn = update ? updateNewsletter : createNewsletter;
        setLoading(true);
        await fn(item);
        setLoading(false);
        setModal(false);
        notify(update ? 'Updated' : 'Created');
    }

    const handleDelete = async (id: number) => {
        setLoading(true);
        await deleteNewsletter(id);
        setLoading(false);
        setModal(false);
        notify('Deleted');
    }

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{update ? 'Update' : 'Create' } Newsletter</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormInput name='name' label='Title' />
                <FormInput name='year' label='Year' disabled />
                <FormInput name='issue' label='Issue' disabled />
                <FileBase64 onDone={handleUploadFile} />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>
                        {createdUpdatedInfo(item)}
                    </Label>
                    <FooterButtonsContainer update={update}>
                        {
                            (update && item) && (
                                <ActionIcon 
                                    type={'delete'} 
                                    onClick={() => handleDelete(item.id)} 
                                    simulateLoading
                                /> 
                            )
                        }
                        <ActionIcon 
                            type={'save'} 
                            onClick={handleSubmit(handleCreateUpdate)} 
                            disabled={!isValid || !isDirty}
                            simulateLoading
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{update?: boolean}>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;